import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ButtonControl,
  IconControl,
  TabPaneControl,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import ActiveSessionListControl, {
  activeSessionId,
} from '../../components/security/ActiveSessionListControl';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import {
  buildClientActiveSessionRequest,
  CLIENT_ACTIVE_SESSION_CATEGORIES,
  CLIENT_ACTIVE_SESSION_ITEM_NAME,
  CLIENT_ACTIVE_SESSION_OPERATIONS,
  runClientActiveSessionOperation,
} from './clientActiveSessionContracts';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

const read = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return undefined;
};

const rows = (workspace) => {
  const value = read(workspace, 'Sessions', 'sessions');
  return Array.isArray(value) ? value : [];
};

const TAB_DEFINITIONS = Object.freeze([
  {
    key: 'app-users',
    title: 'App Users',
    category: CLIENT_ACTIVE_SESSION_CATEGORIES.appUsers,
    emptyMessage: 'No active App User refresh sessions were found for this client.',
  },
  {
    key: 'mcp-server-connections',
    title: 'MCP Server Connections',
    category: CLIENT_ACTIVE_SESSION_CATEGORIES.mcpServerConnections,
    emptyMessage: 'No active MCP OAuth connections or personal access tokens were found for this client.',
  },
]);

const ClientActiveSessionsScreen = ({
  screen,
  clientId,
  currentEnvironment,
}) => {
  const manager = useGenericManager(CLIENT_ACTIVE_SESSION_ITEM_NAME);
  const { canExecute } = useCorePermissions();
  const managerRef = useRef(manager);
  const [activeTab, setActiveTab] = useState(TAB_DEFINITIONS[0].key);
  const [workspace, setWorkspace] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [generation, setGeneration] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  managerRef.current = manager;
  const canRevokeSession = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.revokeClientActiveSession,
  );

  const selectedSessionIsCurrentUser = read(
    selectedSession,
    'IsCurrentUser',
    'isCurrentUser',
  ) === true;

  const activeDefinition = useMemo(
    () => TAB_DEFINITIONS.find((tab) => tab.key === activeTab)
      || TAB_DEFINITIONS[0],
    [activeTab],
  );

  const loadSessions = useCallback(async ({
    page = 0,
    append = false,
    query = '',
    category = CLIENT_ACTIVE_SESSION_CATEGORIES.appUsers,
  } = {}) => {
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setSelectedSession(null);
      setGeneration((value) => value + 1);
    }
    setError('');
    try {
      const loaded = await runClientActiveSessionOperation(
        managerRef.current,
        CLIENT_ACTIVE_SESSION_OPERATIONS.load,
        buildClientActiveSessionRequest({
          clientId,
          currentEnvironment,
          category,
          searchText: query,
          page,
        }),
        'Active authentication sessions are unavailable.',
      );
      if (!loaded.workspace) {
        throw new Error('The active session inventory was not returned.');
      }
      setWorkspace((current) => ({
        ...loaded.workspace,
        Sessions: append
          ? [...rows(current), ...rows(loaded.workspace)]
          : rows(loaded.workspace),
      }));
      return loaded;
    } catch (exception) {
      setError(
        exception?.message || 'Active authentication sessions are unavailable.',
      );
      return null;
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [
    clientId,
    currentEnvironment,
  ]);

  useEffect(() => {
    loadSessions({
      page: 0,
      query: '',
      category: activeDefinition.category,
    });
  }, [activeDefinition.category, clientId, currentEnvironment, loadSessions]);

  const changeTab = useCallback((index, tab, key) => {
    const nextKey = key || tab?.key || TAB_DEFINITIONS[index]?.key
      || TAB_DEFINITIONS[0].key;
    const nextDefinition = TAB_DEFINITIONS.find(
      (candidate) => candidate.key === nextKey,
    ) || TAB_DEFINITIONS[0];
    setActiveTab(nextDefinition.key);
    setSearchText('');
    setSelectedSession(null);
    setWorkspace(null);
    setGeneration((value) => value + 1);
  }, []);

  const searchSessions = useCallback((query) => {
    const nextSearch = String(query || '').trim();
    setSearchText(nextSearch);
    setSelectedSession(null);
    loadSessions({
      page: 0,
      query: nextSearch,
      category: activeDefinition.category,
    });
  }, [activeDefinition.category, loadSessions]);

  const revokeSession = useCallback(async () => {
    if (!canRevokeSession || !selectedSession || selectedSessionIsCurrentUser) {
      return { Success: false };
    }
    setBusy(true);
    setError('');
    try {
      const ended = await runClientActiveSessionOperation(
        managerRef.current,
        CLIENT_ACTIVE_SESSION_OPERATIONS.revoke,
        buildClientActiveSessionRequest({
          clientId,
          currentEnvironment,
          category: activeDefinition.category,
          searchText,
          selectedSession,
        }),
        'The selected active session could not be ended.',
      );
      setWorkspace(ended.workspace);
      setSelectedSession(null);
      setGeneration((value) => value + 1);
      setConfirmOpen(false);
      return ended.result;
    } catch (exception) {
      const message = exception?.message
        || 'The selected active session could not be ended.';
      if (/no longer available/i.test(message)) {
        setSelectedSession(null);
        setGeneration((value) => value + 1);
      }
      setError(message);
      return {
        Success: false,
        DisplayMessage: message,
      };
    } finally {
      setBusy(false);
    }
  }, [
    activeDefinition.category,
    canRevokeSession,
    clientId,
    currentEnvironment,
    searchText,
    selectedSession,
    selectedSessionIsCurrentUser,
  ]);

  const list = (
    <ActiveSessionListControl
      sessions={rows(workspace)}
      selectedSession={selectedSession}
      onSelectedSessionChange={setSelectedSession}
      searchText={searchText}
      onSearchChange={searchSessions}
      loading={loading}
      loadingMore={loadingMore}
      error={error}
      hasMore={read(workspace, 'HasMore', 'hasMore') === true}
      resultGeneration={generation}
      onLoadMore={() => loadSessions({
        page: Number(read(workspace, 'Page', 'page') || 0) + 1,
        append: true,
        query: searchText,
        category: activeDefinition.category,
      })}
      emptyMessage={activeDefinition.emptyMessage}
    />
  );

  const commandBarActions = [
    {
      key: 'refresh-active-sessions',
      label: 'Refresh',
      iconKey: 'refresh',
      loading,
      disabled: loading || busy,
      onClick: () => loadSessions({
        page: 0,
        query: searchText,
        category: activeDefinition.category,
      }),
    },
    ...(canRevokeSession ? [{
      key: 'kill-active-session',
      label: 'Kill Session',
      iconKey: 'delete',
      danger: true,
      loading: busy,
      title: selectedSessionIsCurrentUser
        ? 'You cannot kill your own active session.'
        : undefined,
      disabled: !selectedSession
        || selectedSessionIsCurrentUser
        || busy,
      onClick: () => setConfirmOpen(true),
    }] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry command-active-sessions">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Active Sessions actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <section className="resource-workspace-panel command-active-sessions__results">
        <div className="command-active-sessions__heading">
          <h2>Authentication Sessions</h2>
          <ButtonControl
            type="icon"
            title="About authentication sessions"
            aria-label="About authentication sessions"
            onClick={() => setInfoOpen(true)}
          >
            <IconControl
              iconKey="info"
              title="About authentication sessions"
              width="1rem"
              height="1rem"
            />
          </ButtonControl>
        </div>
        <TabPaneControl
          align="right"
          activeKey={activeTab}
          className="command-active-sessions__tabs"
          compact
          framed={false}
          onChange={changeTab}
          tabs={TAB_DEFINITIONS.map((tab) => ({
            key: tab.key,
            title: tab.title,
            content: tab.key === activeTab ? list : null,
          }))}
          unmountInactive
        />
      </section>
      <DialogControl
        openDialog={infoOpen}
        onCancel={() => setInfoOpen(false)}
        onOk={() => setInfoOpen(false)}
        title="About Authentication Sessions"
        okText="Close"
        hideCancel
        hideCancelButton
      >
        <div className="command-active-sessions__info-dialog">
          <p>
            This screen shows valid App User and MCP Server renewal credentials
            for the selected client.
          </p>
          <dl className="resource-workspace-facts">
            <div>
              <dt>Active</dt>
              <dd>The renewal credential is valid.</dd>
            </div>
            <div>
              <dt>Last activity</dt>
              <dd>
                The recorded time is exact, but it does not mean that a user or
                MCP client is currently online.
              </dd>
            </div>
            <div>
              <dt>History</dt>
              <dd>Revoked and expired sessions remain in Activity &amp; Logs.</dd>
            </div>
          </dl>
        </div>
      </DialogControl>
      <DialogControl
        openDialog={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={revokeSession}
        title="Kill Active Session"
        okText={busy ? 'Ending...' : 'Kill Session'}
        cancelText="Cancel"
        disableOk={busy}
        disableCancel={busy}
        fallbackErrorMessage="The selected active session could not be ended."
      >
        <p>
          End the selected session for{' '}
          {read(selectedSession, 'UserDisplayName', 'userDisplayName')
            || 'this user or MCP client'}? For personal access tokens, this does
          not revoke the PAT; using the active PAT again creates a new session.
        </p>
        <p>
          Session reference: {activeSessionId(selectedSession) || 'not selected'}
        </p>
      </DialogControl>
    </div>
  );
};

export default ClientActiveSessionsScreen;
